import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import SafeSessionStorage from './storage/SafeSessionStorage';

// WARNING: The value of INTERACTION_SESSION_ID_STORAGE_KEY will be used by Analytics Web Client to attach the session ID to events.
// If you change this, you will need to update the Analytics Web Client code as well.
var INTERACTION_SESSION_ID_STORAGE_KEY = 'interactionSession.id';
var INTERACTION_SESSION_ID_PREV_STORAGE_KEY = 'interactionSession.id.prev';
var INTERACTION_SESSION_EXPIRY_STORAGE_KEY = 'interactionSession.expiry';
export var DEFAULT_SESSION_EXPIRY_TIME_MS = 30 * 60 * 1000; // 30 minutes
export var DEFAULT_PERSISTED_SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
export var DEFAULT_EXPIRY_DEBOUNCE = 5 * 1000; // 5 seconds
export var DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE = 0.9; // 90% of expiry

var INTEGER_REGEX = /^\d+$/;
export var INTERACTION_SESSION_ID_UPDATED_EVENT = 'interactionSessionIdUpdated';

/**
 * This class is essentially a wrapper around SessionStorage that introduces some failsafes (such as switching to InMemoryStorage if SessionStorage is not available)
 * and providing resilient methods for accessing and updating the interaction session ID.
 * This class can be initialised anywhere within a document, and `getCurrentInteractionSessionId` will always return the latest interaction session ID.
 *
 * The `setAnalyticsCallback` method must be called with a valid analytics event callback function provided by an Analytics Web Client before any other methods are used.
 * This is to ensure that any generated or renewed session IDs can be reported via an analytics event.
 *
 * The `setInteractionSessionId` method will write the SessionStorage with the persisted session ID, and is only intended to be used by the Analytics Web Client
 */
var InteractionSessionTracking = /*#__PURE__*/function () {
  // This callback function will be called whenever a new session ID is generated
  // It expects a function that sends an operational analytics event, with
  // the first parameter being set to the action ('started' or 'renewed'), and the second parameter being
  // an InteractionSession object containing the new session ID and previous session ID.

  function InteractionSessionTracking() {
    var _this = this;
    _classCallCheck(this, InteractionSessionTracking);
    _defineProperty(this, "analyticsReady", false);
    _defineProperty(this, "generateNewSessionId", function () {
      if (!_this.analyticsReady) {
        return null;
      }
      var currentSessionId = _this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
      var newSessionId = Date.now().toString();
      _this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_STORAGE_KEY, newSessionId);
      var _this$getCurrentSessi = _this.getCurrentSessionParams(),
        isValidSession = _this$getCurrentSessi.isValidSession;
      if (!isValidSession) {
        // If session is not valid at this point, something has gone wrong with Session Storage access
        // Calling sendInteractionSessionEvent now would result in an infinite loop, so we terminate
        return null;
      }
      if (currentSessionId) {
        // Store a copy of the current session ID so we can link sessions if needed
        _this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY, currentSessionId);
        _this.sendInteractionSessionEvent('renewed');
      } else {
        _this.sendInteractionSessionEvent('started');
      }

      // Event listener is in useCrossProductUrl.tsx to update URL query params
      document.dispatchEvent(new Event(INTERACTION_SESSION_ID_UPDATED_EVENT));
      return newSessionId;
    });
    _defineProperty(this, "updateSessionExpiry", function () {
      var expiry = Date.now() + _this.sessionExpiryTime;
      _this.safeSessionStorage.setItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY, expiry.toString());
      return expiry;
    });
    _defineProperty(this, "debouncedUpdateSessionExpiry", function (currentExpiry) {
      var updateSessionExpiry = _this.updateSessionExpiry;
      // If the current expiry is out of date by more than 10% of the expiry, update immediately
      if (currentExpiry < Date.now() + _this.sessionExpiryDebounceThresholdPercentage * _this.sessionExpiryTime) {
        updateSessionExpiry();
      } else {
        if (_this.sessionExpiryDebounceTimer) {
          clearTimeout(_this.sessionExpiryDebounceTimer);
        }
        // @ts-ignore Something wrong with our types... this is legit
        _this.sessionExpiryDebounceTimer = setTimeout(function () {
          updateSessionExpiry();
        }, _this.sessionExpiryDebounce);
      }
    });
    this.sessionExpiryTime = DEFAULT_SESSION_EXPIRY_TIME_MS;
    this.sessionExpiryDebounce = DEFAULT_EXPIRY_DEBOUNCE;
    this.sessionExpiryDebounceThresholdPercentage = DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE;
    this.safeSessionStorage = new SafeSessionStorage();
  }
  return _createClass(InteractionSessionTracking, [{
    key: "getCurrentInteractionSessionId",
    value: function getCurrentInteractionSessionId() {
      try {
        var _this$getCurrentSessi2 = this.getCurrentSessionParams(),
          isValidSession = _this$getCurrentSessi2.isValidSession,
          sessionExpiry = _this$getCurrentSessi2.sessionExpiry,
          sessionId = _this$getCurrentSessi2.sessionId;

        // If we have an existing valid session we can return the sessionId straight away
        if (isValidSession) {
          this.debouncedUpdateSessionExpiry(sessionExpiry);
          return sessionId;
        }

        // To generate a new session ID we must ensure analytics is ready
        if (this.analyticsReady) {
          // Session expiry update must occur before generateNewSessionId. Otherwise we were running
          // into issues of calls attempting to read the session ID before a valid expiry had been set
          this.debouncedUpdateSessionExpiry(sessionExpiry);
          return this.generateNewSessionId();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error retrieving interaction session ID - ".concat(err instanceof Error ? err.message : String(err)));
      }
      return null;
    }
  }, {
    key: "getPreviousInteractionSessionId",
    value: function getPreviousInteractionSessionId() {
      return this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);
    }
  }, {
    key: "setInteractionSessionId",
    value: function setInteractionSessionId(persistedSessionId) {
      if (this.validateSessionId(persistedSessionId)) {
        var currentSessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);

        // If we are already on the required session ID, just return true and make no changes
        // This can happen if the persisted session ID in options is the same as the current session ID
        // We noticed this happening in some cases where Confluence's own URL redirect logic was causing
        // race conditions with our URL stripping, resulting in the same query parameter being processed multiple times
        if (currentSessionId === persistedSessionId) {
          return false;
        }

        // Session expiry update must occur before we fire off the dispatchEvent. Otherwise we were running
        // into issues of the event consumer attempting to read the session ID before a valid expiry had been set
        this.debouncedUpdateSessionExpiry(0);
        this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_STORAGE_KEY, persistedSessionId);
        this.safeSessionStorage.removeItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);
        document.dispatchEvent(new Event(INTERACTION_SESSION_ID_UPDATED_EVENT));
        return true;
      }
      return false;
    }

    // Must be called with a valid sendEvent function for other methods to work
  }, {
    key: "setAnalyticsCallback",
    value: function setAnalyticsCallback(sendEventCallback) {
      if (this.analyticsReady) {
        // If we already have a valid callback, don't over-write it
        return;
      }
      if (sendEventCallback && typeof sendEventCallback === 'function') {
        this.sendEventCallback = sendEventCallback;
        this.analyticsReady = true;
      } else {
        this.analyticsReady = false;
      }
    }
  }, {
    key: "validateSessionId",
    value: function validateSessionId(sessionId) {
      // Check if persisted session ID can parse as valid Unix timestamp
      if (sessionId && INTEGER_REGEX.test(sessionId)) {
        var sessionAge = Date.now() - parseInt(sessionId, 10);

        // Validate that timestamp is not a future time, and is also within the TTL
        if (sessionAge >= 0 && sessionAge <= DEFAULT_PERSISTED_SESSION_TTL_MS) {
          return true;
        }
      }
      return false;
    }

    /*
     * This function retrieves the current session ID and expiry from SessionStorage
     * It runs the necessary checks to ensure the session ID is valid and within expiry
     *
     * It returns the values along with a `validSession` boolean
     */
  }, {
    key: "getCurrentSessionParams",
    value: function getCurrentSessionParams() {
      var sessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
      var sessionExpiry = parseInt(this.safeSessionStorage.getItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY) || '0', 10) || 0;
      if (sessionId && !Number.isNaN(sessionExpiry) && sessionExpiry > Date.now()) {
        return {
          isValidSession: true,
          sessionExpiry: sessionExpiry,
          sessionId: sessionId
        };
      }
      return {
        isValidSession: false,
        sessionExpiry: sessionExpiry,
        sessionId: sessionId
      };
    }

    /*
     * This function sends a GASv3 analytics event indicating the interaction session has started or renewed
     * It requires this.sendEventCallback to be set via Analytics Web Client to a valid operational event sender function
     *
     * Note that Analytics Web Client's callback function will make its own call to getCurrentInteractionSessionId to retrieve the session ID
     * to attach to the event, so a valid session ID/expiry must be retrievable from SessionStorage before this function is called to avoid infinite loops.
     *
     * This is not an ideal solution but it works with the current architecture of Analytics Web Client.
     */
  }, {
    key: "sendInteractionSessionEvent",
    value: function sendInteractionSessionEvent(action) {
      this.sendEventCallback && this.sendEventCallback({
        action: action,
        actionSubject: 'interactionSession',
        source: 'interactionComponent',
        tags: ['navigationExperiences'] // InteractionSession events will be registered under this platform product
      });
    }
  }]);
}();
export { InteractionSessionTracking as default };